import clsx from "clsx";
import { forwardRef, useEffect, useState } from "react";

import { getCurrencyForCountry } from "@web-app/utils/currency";

import { Icon } from "../Icon";
import { Field } from "../Input";
import { Label } from "../Label";

interface CurrencyInputProps {
  countryId?: string;
  currencyOptions?: Array<{
    label: string;
    value: string;
  }>;
  disabled?: boolean;
  invalid?: boolean;
  label?: string;
  onChange: (value: string) => void;
  onCurrencyChange: (currency: string) => void;
  placeholder?: string;
  required?: boolean;
  selectedCurrency: string;
  toolTipText?: string;
  value: string;
}

const defaultCurrencyOptions = [
  { label: "USD", value: "USD" },
  { label: "EUR", value: "EUR" },
  { label: "GBP", value: "GBP" },
  { label: "JPY", value: "JPY" },
  { label: "CAD", value: "CAD" },
  { label: "AUD", value: "AUD" },
];

export const CurrencyInput = forwardRef<HTMLInputElement, CurrencyInputProps>(
  (
    {
      countryId,
      currencyOptions = defaultCurrencyOptions,
      disabled,
      invalid,
      label,
      onChange,
      onCurrencyChange,
      placeholder = "0.00",
      required,
      selectedCurrency,
      toolTipText,
      value,
    },
    ref,
  ) => {
    const [isOpen, setIsOpen] = useState(false);

    useEffect(() => {
      if (countryId) {
        const currency = getCurrencyForCountry(countryId);
        if (
          currency !== selectedCurrency &&
          currencyOptions.some((opt) => opt.value === currency)
        ) {
          onCurrencyChange(currency);
        }
      }
    }, [countryId]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      // Only allow numbers and one decimal point
      if (/^\d*\.?\d*$/.test(newValue)) {
        onChange(newValue);
      }
    };

    return (
      <Field>
        {label && (
          <Label
            required={required}
            toolTipText={toolTipText}
            disabled={disabled}
          >
            {label}
          </Label>
        )}

        <div className="relative flex">
          <div className="relative">
            <button
              type="button"
              onClick={() => setIsOpen(!isOpen)}
              disabled={disabled}
              className={clsx(
                "flex h-full items-center gap-1 rounded-l-md border text-xs md:text-sm px-3 py-2",
                invalid
                  ? "border-red-300 text-red-900"
                  : "border-gray-300 text-gray-900",
                "bg-gray-50",
                "disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-500",
              )}
            >
              <span className=" text-xs md:text-sm">{selectedCurrency}</span>
              <Icon name="chevronDown" className="h-3 md:h-4 w-3 md:w-4" />
            </button>

            {isOpen && (
              <div className="absolute left-0 z-10 mt-1 max-h-60 w-fit overflow-auto rounded-md bg-white py-1 text-xs md:text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                {currencyOptions.map((option) => (
                  <button
                    key={option.value}
                    className={clsx(
                      "relative w-full cursor-default select-none py-2 pl-3 pr-9 text-left",
                      selectedCurrency === option.value
                        ? "bg-blue-50 text-blue-900"
                        : "text-gray-900 hover:bg-gray-50",
                    )}
                    onClick={() => {
                      onCurrencyChange(option.value);
                      setIsOpen(false);
                    }}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          <input
            ref={ref}
            type="text"
            value={value}
            onChange={handleInputChange}
            disabled={disabled}
            placeholder={placeholder}
            className={clsx(
              "block w-full rounded-r-md border px-3 py-2 text-gray-900 shadow-sm text-xs md:text-sm",
              invalid
                ? "border-red-300 focus:border-red-500 focus:ring-red-500"
                : "border-gray-300 focus:border-blue-500 focus:ring-blue-500",
              "focus:outline-none focus:ring-2 focus:ring-opacity-50",
              "disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-500",
              "-ml-px", // Remove double border between currency select and input
            )}
          />
        </div>
      </Field>
    );
  },
);

CurrencyInput.displayName = "CurrencyInput";
